import type { Ref } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import BlockRounded from '@mui/icons-material/BlockRounded';

/** Sentinel option id for the "— No line —" choice when `allowNone` is set. */
export const LINE_PICKER_NONE = '__none__';

export interface LinePickerLine {
  id: string;
  phone: string;
  /** Device model on the line (e.g. "iPhone 14"). */
  device: string;
  carrier: string;
  /** Pre-formatted secondary caption (e.g. "contract ends Aug 11, 2026 · assigned to Marcus Reilly"). */
  secondary?: string;
  /** Ineligible rows render dimmed + non-selectable with {@link disabledNote}. */
  disabled?: boolean;
  disabledNote?: string;
}

export interface LinePickerProps {
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Selected line id; '' = none chosen, {@link LINE_PICKER_NONE} = explicit "no line". */
  value: string;
  onChange: (id: string) => void;
  lines: LinePickerLine[];
  /** Prepend a "— No line —" option (for optional-line flows). */
  allowNone?: boolean;
  noneLabel?: string;
  noOptionsText?: string;
  helperText?: string;
  ref?: Ref<HTMLDivElement>;
}

function CarrierTag({ carrier }: { carrier: string }) {
  const theme = useTheme();
  const tone = carrier.toLowerCase() === 'verizon' ? theme.palette.error : theme.palette.info;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        p: '1px 7px',
        borderRadius: 0.5,
        bgcolor: alpha(tone.main, 0.1),
        color: tone.dark,
        border: `1px solid ${alpha(tone.main, 0.28)}`,
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {carrier}
    </Box>
  );
}

/**
 * Line selector (the Polaris kit `LinePicker`) — an MUI `Autocomplete` with rich
 * option rows (phone · device · carrier tag, and a secondary contract/assignee
 * caption). Ineligible lines (e.g. not upgrade-eligible) render dimmed + inert
 * with an explanatory note. Optionally offers a "— No line —" first choice.
 * Presentational: the caller hydrates `lines` from `GET /api/v1/lines`.
 */
export function LinePicker({
  label,
  placeholder,
  required,
  disabled,
  loading,
  value,
  onChange,
  lines,
  allowNone,
  noneLabel = '— No line —',
  noOptionsText,
  helperText,
  ref,
}: LinePickerProps) {
  const noneOption: LinePickerLine = {
    id: LINE_PICKER_NONE,
    phone: noneLabel,
    device: '',
    carrier: '',
  };
  const options = allowNone ? [noneOption, ...lines] : lines;
  const selected = options.find((o) => o.id === value) ?? null;

  return (
    <Autocomplete<LinePickerLine>
      ref={ref}
      options={options}
      value={selected}
      loading={loading}
      disabled={disabled}
      noOptionsText={noOptionsText}
      getOptionDisabled={(o) => !!o.disabled}
      getOptionLabel={(o) =>
        o.id === LINE_PICKER_NONE ? o.phone : `${o.phone} · ${o.device} · ${o.carrier}`
      }
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(_e, next) => onChange(next?.id ?? '')}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: string };
        if (option.id === LINE_PICKER_NONE) {
          return (
            <Box component="li" key={key} {...rest}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{option.phone}</Typography>
            </Box>
          );
        }
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{
              opacity: option.disabled ? 0.55 : 1,
              flexDirection: 'column',
              alignItems: 'flex-start !important',
              gap: 0.25,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {option.phone}
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                · {option.device}
              </Typography>
              <CarrierTag carrier={option.carrier} />
            </Box>
            {option.secondary && (
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                {option.secondary}
              </Typography>
            )}
            {option.disabled && option.disabledNote && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 11.5,
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                <BlockRounded sx={{ fontSize: 12 }} />
                {option.disabledNote}
              </Box>
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={label}
          placeholder={placeholder}
          required={required}
          helperText={helperText}
        />
      )}
    />
  );
}
